import React, { useEffect } from 'react';
import FormBuilder from '../../components/formBuilder';
import { FORMS } from '../../schemas/stepMap';
import { useProperty } from '../../hooks/useProperty';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import AnalysisSection from './AnalysisSection';
import { getHeaderMessage, getMetricsData } from './helpers';
import PropertyMap from '../../components/PropertyMap';
import PictureGallery from '../../components/PictureGallery';
import { useUser } from '../../hooks/useUser';
import { logEvent } from 'firebase/analytics';
import { analytics } from '../../../firebase/clientApp';

function isExampleProperty(property) {
  return property.isExampleProperty === true;
}

function isFirsTimeVisitsNonExampleProperty() {
  if (localStorage.getItem('visited_created_property_first_time') === null) {
    return true;
  }
  return false;
}

function markNenExamplePropertyVisited() {
  localStorage.setItem('visited_created_property_first_time', true);
}

export default function PropertyOverview() {
  const { propertyData, scenarios, loading } = useProperty();
  const { user, userDocData } = useUser();
  const scenario = scenarios?.find((s) => !s.isDefault) || {};

  useEffect(() => {
    if (!loading && propertyData && !isExampleProperty(propertyData)) {
      if (isFirsTimeVisitsNonExampleProperty()) {
        logEvent(analytics, 'visited_created_property_first_time', {
          user_id: user?.uid,
          email: userDocData?.email,
          property_id: propertyData?.propertyId,
        });
        markNenExamplePropertyVisited();
      }
    }
  }, [loading, propertyData]);

  const { propertyId } = useParams();

  if (!propertyData) {
    return null;
  }

  const { purchaseWorksheetSubmitted, flipWorksheetSubmitted, projectType } =
    propertyData || {};

  const { rehabWorksheetSubmitted, refinancingWorksheetSubmitted } =
    scenario || {};

  const isFlip = projectType === 'flip';

  const form = FORMS.propertyDescription;

  const { title, description } = getHeaderMessage(
    propertyData,
    scenario,
    isFlip
  );
  const { flipMetrics, purchaseMetrics, rehabMetrics, refinancingMetrics } =
    getMetricsData(propertyData, scenario, isFlip);

  return (
    <>
      <Helmet>
        <title>Property Overview</title>
      </Helmet>
      <div className="flex flex-col gap-6">
        <section
          id="summary"
          className="w-full flex flex-col rounded-lg bg-white"
        >
          <div className="flex justify-between items-center border-b border-neutral-50 px-5 py-5">
            <div className="flex flex-col items-start justify-center">
              <h2 className="text-4xl font-serif font-medium text-neutral-900">
                {title}
              </h2>
              <p className="text-md font-normal text-neutral-500">
                {description}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3">
            {isFlip && (
              <AnalysisSection
                header="Flip Analysis"
                metricsData={flipMetrics}
                description="Complete the flip worksheet to view an analysis of the property on flip"
                actionLink={
                  flipWorksheetSubmitted
                    ? FORMS.flipAnalysis.pathName
                    : FORMS.flipWorksheet.pathName
                }
                completed={flipWorksheetSubmitted}
                first
              />
            )}
            {!isFlip && (
              <AnalysisSection
                header="Purchase Analysis"
                description="Complete the purchase worksheet to view an analysis of the property on purchase, before rehab or refinancing"
                metricsData={purchaseMetrics}
                actionLink={
                  purchaseWorksheetSubmitted
                    ? FORMS.purchaseAnalysis.pathName
                    : FORMS.purchaseWorksheet.pathName
                }
                completed={purchaseWorksheetSubmitted}
                metricsFormSchema={FORMS.purchaseCriteria}
                metricsEvalSchema={FORMS.purchaseAnalysis.investmentMetrics}
                data={{ ...userDocData, ...propertyData }}
                first
              />
            )}

            {!isFlip && (
              <AnalysisSection
                header="Rehab Analysis"
                middle
                metricsData={rehabMetrics}
                description="Complete the rehab worksheet to view an analysis of the property after rehab, but before refinancing, also known as the Buy & Hold Analysis"
                actionLink={
                  rehabWorksheetSubmitted
                    ? FORMS.BuyAndHoldAnalysis.pathName
                    : FORMS.rehabWorksheet.pathName
                }
                completed={rehabWorksheetSubmitted}
                prevCompleted={purchaseWorksheetSubmitted}
                metricsFormSchema={FORMS.buyAndHoldCriteria}
                metricsEvalSchema={FORMS.BuyAndHoldAnalysis.investmentMetrics}
                data={{ ...userDocData, ...propertyData, ...scenario }}
              />
            )}
            {!isFlip && (
              <AnalysisSection
                header="BRRRR Analysis"
                description="Complete the refinancing worksheet to view an analysis of the property after refinancing, also known as the BRRRR Analysis"
                metricsData={refinancingMetrics}
                actionLink={
                  refinancingWorksheetSubmitted
                    ? FORMS.BRRRRAnalysis.pathName
                    : FORMS.refinancing.pathName
                }
                metricsFormSchema={FORMS.refinanceCriteria}
                metricsEvalSchema={FORMS.BRRRRAnalysis.investmentMetrics}
                completed={refinancingWorksheetSubmitted}
                prevCompleted={rehabWorksheetSubmitted}
                data={{ ...userDocData, ...propertyData, ...scenario }}
              />
            )}
          </div>
        </section>
        <section className="max-w-full">
          <div className="grid grid-cols-1 md:grid-cols-[44%_56%] min-h-[313px]">
            <div className="mb-4 md:mb-0 md:mr-4 h-full">
              <PictureGallery />
            </div>

            <PropertyMap />
          </div>
        </section>
        <section id="property-details">
          <FormBuilder
            formSchema={form.schema}
            formType={form.formType}
            data={propertyData}
            docId={propertyId}
          />
        </section>
      </div>
    </>
  );
}
